import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { User } from 'firebase/auth';

interface UserContextType {
  isPremium: boolean;
  isAdmin: boolean;
  currentUser: User | null;
  setIsPremium: (value: boolean) => void;
  isLoading: boolean;
  refreshUserData: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  isPremium: false,
  isAdmin: false,
  currentUser: null,
  setIsPremium: () => {},
  isLoading: true,
  refreshUserData: async () => {}
});

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [isPremium, setIsPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const checkUserStatus = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      setIsPremium(false);
      setIsAdmin(false);
      return;
    }

    try {
      // Check if global free mode is enabled
      const globalFreeMode = import.meta.env.VITE_GLOBAL_FREE_MODE === 'true';
      
      // Get user document
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      // Check if user is premium or has admin status
      const isAdminUser = userData?.isAdmin === true;
      const hasPremiumSubscription = userData?.subscriptionStatus === 'active';
      
      setIsAdmin(isAdminUser);
      // If global free mode is enabled, everyone gets premium access
      setIsPremium(globalFreeMode || isAdminUser || hasPremiumSubscription);
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking user status:', error);
      setIsPremium(false);
      setIsAdmin(false);
      setIsLoading(false);
    }
  }, [user]);

  // Function to refresh user data after subscription changes
  const refreshUserData = useCallback(async () => {
    setIsLoading(true);
    await checkUserStatus();
  }, [checkUserStatus]);

  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  return (
    <UserContext.Provider value={{
      isPremium,
      isAdmin,
      currentUser: user,
      setIsPremium,
      isLoading,
      refreshUserData
    }}>
      {children}
    </UserContext.Provider>
  );
}
